import NapsterSpinner from '@components/NapsterSpinner';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { MyCreation, getCreationTrack } from '../../services/myCreationsApi';

export const CreationViewerPage = () => {
  const { t } = useTranslation();
  const { assetType, orgId, creationId } = useParams<{
    assetType: string;
    orgId: string;
    creationId: string;
  }>();

  const [creationData, setCreationData] = useState<MyCreation | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // For track type, the music player handles loading via useAppRouting
  // So we don't need to show a loader here
  const isTrackType = assetType === 'track';

  useEffect(() => {
    // Skip fetching for track type - music player handles it
    if (isTrackType) {
      setIsLoading(false);
      return;
    }

    const fetchCreation = async () => {
      if (!orgId || !creationId) {
        setError(t('creationViewer.invalidUrl'));
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        const data = await getCreationTrack(orgId, creationId);

        // Check if it's an error response
        if ('error' in data) {
          setError(t('creationViewer.noLongerAvailable'));
          setCreationData(null);
        } else {
          setCreationData(data);
          setError(null);
        }
      } catch (err) {
        console.error('Error fetching creation:', err);
        setError(t('creationViewer.failedToLoad'));
      } finally {
        setIsLoading(false);
      }
    };

    fetchCreation();
  }, [orgId, creationId, isTrackType]);

  // For track type, return null - music player handles everything
  if (isTrackType) {
    return null;
  }

  if (isLoading) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <NapsterSpinner size="large" />
      </div>
    );
  }

  if (error || !creationData) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <div className="text-center">
          <p className="text-white text-lg mb-2">
            {error || t('creationViewer.notFound')}
          </p>
        </div>
      </div>
    );
  }

  const creationInfo = creationData.data;
  const contributor = creationData.contributor;

  return (
    <div className="w-full h-full overflow-auto na-scroll-container bg-black pt-[60px]">
      {/* Asset Type Specific Rendering */}
      {assetType === 'image' && (
        <div className="flex flex-col w-full">
          {/* Full Width Image */}
          <div className="w-full bg-black flex items-center justify-center">
            <img
              src={creationInfo.url || creationInfo.previewImage}
              alt={creationInfo.title}
              className="w-full h-auto object-contain max-h-[70vh]"
            />
          </div>

          {/* Details at Bottom */}
          <div className="px-4 sm:px-6 py-6 bg-black">
            <div className="w-full">
              {/* Contributor Section and Title/Description - Single Line */}
              <div className="mt-6 pt-6 border-t border-white/10 flex items-start justify-between gap-6">
                {/* Left Side - Contributor */}
                {contributor && (
                  <div className="flex items-center gap-3 flex-shrink-0">
                    {contributor.previewImage && (
                      <img
                        src={contributor.previewImage}
                        alt={contributor.fullName}
                        className="w-10 h-10 rounded-full object-cover"
                      />
                    )}
                    <div>
                      <p className="text-white text-sm font-medium">
                        {contributor.fullName}
                      </p>
                    </div>
                  </div>
                )}

                {/* Right Side - Title and Description */}
                <div className="flex-1 text-right">
                  <h1 className="text-white text-2xl sm:text-3xl font-bold">
                    {creationInfo.title}
                  </h1>
                  {creationInfo.description && (
                    <p className="text-white/70 text-sm sm:text-base leading-relaxed">
                      {creationInfo.description}
                    </p>
                  )}
                </div>
              </div>

              {/* Metadata */}
              {creationInfo.genres && creationInfo.genres.length > 0 && (
                <div className="mt-6 pt-6 border-t border-white/10">
                  <p className="text-white/50 text-xs uppercase tracking-wider mb-3">
                    {t('creationViewer.genres')}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {creationInfo.genres.map((genre: string, idx: number) => (
                      <span
                        key={idx}
                        className="px-2 py-1 bg-white/5 rounded text-white/70 text-xs">
                        {genre}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Artifact/Text Type */}
      {assetType === 'artifact' && (
        <div className="px-4 sm:px-6 py-6 bg-black">
          <div className="w-full">
            {/* Title */}
            <h1 className="text-white text-2xl sm:text-3xl font-bold mb-6">
              {creationInfo.title}
            </h1>

            {/* Content */}
            <div className="bg-white/5 rounded-lg p-6 mb-8">
              <div className="text-white/80 text-base leading-relaxed whitespace-pre-wrap">
                {creationInfo.content || creationInfo.description}
              </div>
            </div>

            {/* Contributor Section - Single Line */}
            {contributor && (
              <div className="mt-6 pt-6 border-t border-white/10 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {contributor.previewImage && (
                    <img
                      src={contributor.previewImage}
                      alt={contributor.fullName}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                  )}
                  <p className="text-white/50 text-xs uppercase tracking-wider">
                    {t('creationViewer.createdBy')}
                  </p>
                </div>
                <p className="text-white text-sm font-medium">
                  {contributor.fullName}
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CreationViewerPage;
